import { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import {
  Box,
  Flex,
  Heading,
  Spinner,
  Button,
  useToast,
  ModalContent,
  ModalOverlay,
  Modal,
  ModalBody,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import {
  newBorder,
  primary,
  white,
  textSecondary,
} from "../../../../utils/colors";
import { useTranslation } from "react-i18next";
// import useRoleCheck from "../../../../hooks/useRoleCheck";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import useThemeColors from "../../../../hooks/useThemeColors";
import useScrollToTop from "../../../../hooks/useScrollToTop";
import SidePanel from "../../../../components/sidepanelNew/SidePanel";
import {
  listTenantDetailsBySlugAction,
  updateTenantSubscriptionStatusAction,
} from "../../../../actions/tenantActions";
import Module from "./partials/Module";
import { TENANT_UPDATE_SUBSCRIPTION_STATUS_RESET } from "../../../../constants/tenantConstants";
import ConfirmStatusUpdateModal from "../../../../components/modals/ConfirmStatusUpdateModal";
import ManageSubscriptionSidepanel from "../../payments/checkout/ManageSubscriptionSidepanel";
import CheckoutModalStripe from "../../payments/checkout/CheckoutModalStripe";
import CheckoutPlans from "../../payments/checkout/partials/CheckoutPlans";
import { PAYMENT_TENANT_CANCEL_SUBSCRIPTION_RESET } from "../../../../constants/paymentConstants";
import UserNoAccess from "../../../../components/partials/UserNoAccess";

const ActiveModules = () => {
  useScrollToTop();

  // GENERAL
  const { slug } = useParams();
  const dispatch = useDispatch();
  // const toastComponent = ToastComponent();
  const { t } = useTranslation();
  const toast = useToast();
  const location = useLocation();
  const navigate = useNavigate();

  const [monthly, setMonthly] = useState(true);
  const [currentCurrency, setCurrentCurrency] = useState("EUR");

  // COLORS
  const { whiteMode, backgroundLightMode, textPrimaryMode } = useThemeColors();

  // TAB BUTTONS
  // Button details for mapping
  const buttonDetails = [
    {
      label: t("clinicSettings.clinicInfo"),
      link: `/${slug}/settings/clinic-settings`,
      isDisabled: false,
    },
    {
      label: t("clinicSettings.activeModules"),
      link: `/${slug}/settings/clinic-settings/active-modules`,
      isDisabled: false,
    },
    {
      label: t("clinicSettings.paymentsInvoices"),
      link: `/${slug}/settings/clinic-settings/payments-and-invoices`,
      isDisabled: false,
    },
    {
      label: t("clinicSettings.tenantUsers"),
      link: `/${slug}/settings/clinic-settings/users`,
      isDisabled: false,
    },
  ];

  // STATE
  const [checkoutPanelOpen, setCheckoutPanelOpen] = useState(false);
  const [subscriptionPanelOpen, setSubscriptionPanelOpen] = useState(false);

  const [showUpdateStatusConfirmation, setShowUpdateStatusConfirmation] =
    useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [selectedTenantID, setSelectedTenantID] = useState(null);

  const [selectedStripePriceId, setSelectedStripePriceId] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);

  // REDUX
  const authLogin = useSelector((state) => state.authLogin);
  const { userInfo } = authLogin;

  const tenantDetailsBySlug = useSelector((state) => state.tenantDetailsBySlug);
  const {
    loading: loadingTenantDetails,
    error: errorTenantDetails,
    success: successTenantDetails,
    tenant,
  } = tenantDetailsBySlug;

  const tenantUpdateSubscriptionStatus = useSelector(
    (state) => state.tenantUpdateSubscriptionStatus
  );
  const {
    loading: loadingUpdateSubscriptionStatus,
    error: errorUpdateSubscriptionStatus,
    success: successUpdateSubscriptionStatus,
  } = tenantUpdateSubscriptionStatus;

  const paymentUpdateSubscriptionBillingAndCreateCheckout = useSelector(
    (state) => state.paymentUpdateSubscriptionBillingAndCreateCheckout
  );
  const {
    loading: loadingCheckout,
    error: errorCheckout,
    success: successCheckout,
    checkoutUrl,
  } = paymentUpdateSubscriptionBillingAndCreateCheckout;

  const paymentTenantCancelSubscription = useSelector(
    (state) => state.paymentTenantCancelSubscription
  );
  const {
    loading: loadingCancelSubscription,
    error: errorCancelSubscription,
    success: successCancelSubscription,
  } = paymentTenantCancelSubscription;

  // HANDLERS

  const closeCheckoutPanel = () => {
    setCheckoutPanelOpen(false);
    setSelectedSubscription(null);
    setSelectedStripePriceId(null);
    setSelectedPlan(null);
  };

  // HANDLER: Show Confirmation Modal Before Updating Subscription
  const confirmUpdateSubscriptionStatusHandler = (
    tenantID,
    subscriptionName,
    isActive
  ) => {
    setSelectedTenantID(tenantID);
    setSelectedSubscription({
      name: subscriptionName,
      status: isActive ? "active" : "inactive",
    });
    setShowUpdateStatusConfirmation(true);
  };

  // HANDLER: Proceed with Subscription Status Update After Confirmation
  const handleConfirmStatusUpdate = () => {
    if (selectedTenantID && selectedSubscription) {
      updateSubscriptionStatusHandler(
        selectedTenantID,
        selectedSubscription.name,
        selectedSubscription.status === "active"
      );
    }
    setShowUpdateStatusConfirmation(false);
  };

  const updateSubscriptionStatusHandler = (
    tenantID,
    subscriptionName,
    isActive
  ) => {
    const newStatus = isActive ? "active" : "inactive";
    dispatch(
      updateTenantSubscriptionStatusAction(
        slug,
        tenantID,
        subscriptionName,
        newStatus
      )
    );
  };

  // FETCH DATA
  // useEffect(() => {
  //   const queryParams = new URLSearchParams(location.search);
  //   const reloadTenant = queryParams.get("reloadTenant");

  //   if (!tenant || reloadTenant) {
  //     dispatch(listTenantDetails(userInfo.tenantID));

  //     // ✅ Remove query param after reload to prevent infinite loop
  //     if (reloadTenant) {
  //       navigate("/settings/clinic-settings/active-modules", { replace: true });
  //     }
  //   }
  // }, [dispatch, userInfo.tenantID, location.search]);
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const reloadTenant = queryParams.get("reloadTenant");

    // Fetch tenant details based on slug if not already fetched or if reloadTenant is present
    if (!tenant || reloadTenant) {
      dispatch(listTenantDetailsBySlugAction(slug)); // Use slug to fetch tenant details

      // If reloadTenant exists in the query params, reload and prevent infinite loop
      if (reloadTenant) {
        navigate(`/${slug}/settings/clinic-settings/active-modules`, {
          replace: true,
        }); // Correct URL with slug
      }
    }
  }, [dispatch, slug, location.search, tenant, navigate]);

  // USEEFFECT
  useEffect(() => {
    if (successUpdateSubscriptionStatus) {
      toast({
        title: t("toasts.subscriptionStatusUpdated"),
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } else if (errorUpdateSubscriptionStatus) {
      toast({
        title: t("toasts.subscriptionStatusUpdatedError"),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }

    dispatch({ type: TENANT_UPDATE_SUBSCRIPTION_STATUS_RESET });
  }, [successUpdateSubscriptionStatus, errorUpdateSubscriptionStatus]);

  useEffect(() => {
    if (successCancelSubscription) {
      toast({
        title: t("module.subscriptionCancelled"),
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      // dispatch({ type: PAYMENT_TENANT_SETUP_INTENT_RESET });
      // dispatch({
      //   type: PAYMENT_TENANT_CANCEL_SUBSCRIPTION_RESET,
      // });
      // dispatch(listTenantDetails());

      dispatch(listTenantDetailsBySlugAction(slug));
      // closeHandler();
    }
    if (errorCancelSubscription) {
      toast({
        title: t("module.error"),
        description: errorCancelSubscription,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }

    dispatch({
      type: PAYMENT_TENANT_CANCEL_SUBSCRIPTION_RESET,
    });
  }, [successCancelSubscription, errorCancelSubscription]);

  // CHECK PERMISSIONS

  const currentTenantSlug = slug || tenant?.slug;

  // 1. Get permissions for current tenant
  const currentTenantAccess = userInfo?.tenants?.find(
    (t) => t.slug === currentTenantSlug
  );

  const hasAccess = currentTenantAccess?.permissions?.settings?.modules;

  // const hasAccessToCreate = currentTenantAccess?.permissions?.patients?.create;

  useEffect(() => {
    // Early exit if permissions are not loaded yet
    if (!currentTenantAccess?.permissions) return;

    const perms = currentTenantAccess.permissions;

    if (perms.settings?.modules) return; // ✅ allowed, stay on dashboard
  }, [currentTenantAccess, slug]);

  // if (hasAccess === false)
  //   return (
  //     <>
  //       <UserNoAccess pageName={t("noAccess.subscriptions")} />
  //     </>
  //   );

  // LOGS
  // console.log("tenant", tenant);
  // console.log("modules", tenant?.subscriptions);
  // console.log("selectedSubscription", selectedSubscription);

  return (
    <>
      {/* HELMET START */}
      <Helmet>
        <title>{t("pageTitle.activeModules")}</title>
        <meta name="description" content={t("pageDescription.activeModules")} />
      </Helmet>
      {/* HELMET END */}

      {/* SIDEPANELS  START */}
      {/* {checkoutPanelOpen && (
        <SidePanel
          open={checkoutPanelOpen}
          close={() => setCheckoutPanelOpen(false)}
          title={t("tenant.upgradeToPremium")}
          subTitle={t("tenant.upgradeToPremiumSub")}
          noBorder={false}
        >
          <CheckoutSidepanelSimpleStripe
            slug={slug}
            selectedSubscription={selectedSubscription}
            setSidePanelOpen={setCheckoutPanelOpen}
          />
        
        </SidePanel>
      )}   */}

      {/* MODALS START */}
      {checkoutPanelOpen && (
        <Modal
          isOpen={checkoutPanelOpen}
          onClose={closeCheckoutPanel}
          isCentered
        >
          <ModalOverlay />
          <ModalContent
            width="1100px"
            maxW="1100px"
            minW="1100px"
            borderRadius="2rem"
            maxH="90vh"
            overflowY="auto"
          >
            <ModalBody p="0rem 3rem 3rem" m="0">
              {!loadingCheckout &&
              !loadingTenantDetails &&
              !loadingUpdateSubscriptionStatus ? (
                <>
                  {selectedPlan === null ? (
                    <CheckoutPlans
                      tenant={tenant}
                      slug={slug}
                      selectedSubscrition={selectedSubscription}
                      setSelectedSubscription={setSelectedSubscription}
                      selectedStripePriceId={selectedStripePriceId}
                      setSelectedStripePriceId={setSelectedStripePriceId}
                      setCheckoutPanelOpen={setCheckoutPanelOpen}
                      selectedPlan={selectedPlan}
                      setSelectedPlan={setSelectedPlan}
                      monthly={monthly}
                      setMonthly={setMonthly}
                      currentCurrency={currentCurrency}
                      setCurrentCurrency={setCurrentCurrency}
                    />
                  ) : (
                    <CheckoutModalStripe
                      tenant={tenant}
                      slug={slug}
                      selectedSubscription={selectedSubscription}
                      setSelectedSubscription={setSelectedSubscription}
                      setCheckoutPanelOpen={setCheckoutPanelOpen}
                      setSelectedStripePriceId={setSelectedStripePriceId}
                      selectedPlan={selectedPlan}
                      setSelectedPlan={setSelectedPlan}
                      monthly={monthly}
                      setMonthly={setMonthly}
                      currentCurrency={currentCurrency}
                      setCurrentCurrency={setCurrentCurrency}
                    />
                  )}
                </>
              ) : (
                <Flex
                  w="100%"
                  h="100%"
                  minH="600px"
                  align="center"
                  justify="center"
                  // p="2rem 0"
                  direction="column"
                >
                  <Spinner size="xl" color={primary} />
                </Flex>
              )}
            </ModalBody>
          </ModalContent>
        </Modal>
      )}

      {subscriptionPanelOpen && (
        <SidePanel
          open={subscriptionPanelOpen}
          close={() => setSubscriptionPanelOpen(false)}
          title={t("tenant.subscriptionDetails")}
          subTitle={t("tenant.subscriptionDetailsSub")}
          noBorder={false}
        >
          <ManageSubscriptionSidepanel
            slug={slug}
            selectedSubscription={selectedSubscription}
            setSidePanelOpen={setSubscriptionPanelOpen}
          />
        </SidePanel>
      )}
      {/* SIDEPANELS  END */}

      {/* MODALS START */}
      {showUpdateStatusConfirmation && (
        <ConfirmStatusUpdateModal
          headerTitle={t("module.updateStatusTitle")}
          title={
            selectedSubscription?.status === "active"
              ? t("module.toggleActive", {
                  moduleName: t(`module.title-${selectedSubscription?.name}`),
                })
              : t("module.toggleInactive", {
                  moduleName: t(`module.title-${selectedSubscription?.name}`),
                })
          }
          moduleName={t(`module.title-${selectedSubscription?.name}`)}
          isActive={selectedSubscription?.status === "active"}
          warningLabel={
            selectedSubscription?.status === "active"
              ? t("module.updateStatusWarningActive")
              : t("module.updateStatusWarningInactive")
          }
          isOpen={showUpdateStatusConfirmation}
          onClose={() => setShowUpdateStatusConfirmation(false)}
          confirmAction={handleConfirmStatusUpdate}
        />
      )}

      {/* MODALS END */}

      {/* MAIN START */}
      {tenant ? (
        <Box w="100%" minH="100%" pt="80px">
          <Flex
            direction="column"
            w="100%"
            p={["1rem 1rem 6rem", "2rem 2rem 6rem", "2rem 2rem 6rem"]}
          >
            <Box
              w="100%"
              h="100%"
              bg={whiteMode}
              overflow="hidden"
              borderRadius="2rem"
            >
              {/* HEADER START */}
              <Flex
                w="100%"
                direction="column"
                p="0rem 2rem 1rem"
                m="0 0 0rem"
                borderBottom={`1px solid ${newBorder}`}
              >
                <Flex
                  w="100%"
                  align="center"
                  justify="space-between"
                  borderBottom={`0px solid ${newBorder}`}
                  p="2rem 0rem 0.5rem"
                >
                  <Heading fontSize="1.25rem" fontWeight="600">
                    {t("clinicSettings.clinicSettings")}
                  </Heading>
                </Flex>

                {/* TABS */}
                <Flex w="100%" gap="1rem" m="1rem 0 0">
                  {buttonDetails.map((button, index) => {
                    // Check if the current location matches the button link
                    const isActive = location.pathname === button.link;

                    return (
                      <Link to={button.link} key={index}>
                        <Button
                          variant="primary"
                          size="sm"
                          h="2.5rem"
                          px="1.5rem"
                          bg={isActive ? textPrimaryMode : backgroundLightMode}
                          color={isActive ? white : textSecondary}
                          borderRadius="1rem"
                          fontWeight="500"
                          isDisabled={button.isDisabled}
                          _hover={{
                            bg: isActive ? textPrimaryMode : "gray.100",
                            color: isActive ? white : textSecondary,
                          }}
                        >
                          {button.label}
                        </Button>
                      </Link>
                    );
                  })}
                </Flex>
              </Flex>
              {/* HEADER END */}

              {hasAccess ? (
                <>
                  {/* MAIN LAYOUT START */}
                  {tenant ? (
                    <Flex w="100%" direction="column" p="0 2rem 2rem">
                      {tenant.subscriptions.map((module) => (
                        <Module
                          key={module.name}
                          nextBillingDate={module.nextBillingDate}
                          nextUsageResetDate={module.nextUsageResetDate}
                          stripeSubscriptionId={module.stripeSubscriptionId}
                          stripeSubscriptionStatus={
                            module.stripeSubscriptionStatus
                          }
                          name={module.name}
                          plan={module.plan}
                          subscriptionStartDate={module.subscriptionStartDate}
                          subscriptionEndDate={module.subscriptionEndDate}
                          tenant={tenant}
                          stripePriceId={module.stripePriceId}
                          title={t(`module.title-${module.name}`)}
                          description={t(`module.description-${module.name}`)}
                          setCheckoutPanelOpen={() => {
                            setSelectedSubscription(module); // Store full subscription details
                            setCheckoutPanelOpen(true);
                          }}
                          setSubscriptionPanelOpen={() => {
                            setSelectedSubscription(module); // Store full subscription details
                            setSubscriptionPanelOpen(true);
                          }}
                          updateStatus={(
                            tenantID,
                            subscriptionName,
                            isActive
                          ) =>
                            confirmUpdateSubscriptionStatusHandler(
                              tenantID,
                              subscriptionName,
                              isActive
                            )
                          } // ✅ Pass confirmation modal trigger instead of direct update
                        />
                      ))}
                    </Flex>
                  ) : (
                    <Flex w="100%" h="100%" align="center" justify="center">
                      <Spinner size="xl" color={primary} />
                    </Flex>
                  )}
                </>
              ) : (
                <UserNoAccess
                  pageName={t("noAccess.subscriptions")}
                  p="6rem 0 6rem"
                />
              )}
              {/* <Flex w="100%" direction="column" p="0 2rem 8rem">
              <Module
                tenants={tenant}
                title={t("module.treatmentPlans")}
                description={t("module.treatmentPlansText")}
              />
              <Module
                tenants={tenant}
                title={t("module.supplies")}
                description={t("module.suppliesText")}
              />
              <Module
                tenants={tenant}
                title={t("module.appointments")}
                description={t("module.appointmentsText")}
              />
            </Flex> */}
              {/* MAIN LAYOUT  END */}
            </Box>
          </Flex>
        </Box>
      ) : (
        <Flex w="100%" h="100vh" align="center" justify="center">
          <Spinner size="xl" color={primary} />
        </Flex>
      )}
      {/* MAIN END */}
    </>
  );
};

export default ActiveModules;
